import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { apm } from '@elastic/apm-rum';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import config from '../../config';
import { areFeaturesFetched } from '../../selectors/features';
import { getUser } from '../../selectors/user';
import { SupportedLanguages } from '../AppLanguage/constants';
import { ZendeskVisibilityContext } from './ZendeskVisibilityContext';

const ZENDESK_CONVERSATION_LOCALE_KEY = 'zendeskConversationLocale';
const ZENDESK_LAZY_LOAD_TIMEOUT = 10000;
const ZENDESK_LOAD_TIMEOUT = 5000;
const ZENDESK_OPEN_TIMEOUT = 5000;

type Props = {
  children: React.ReactNode;
};

const isZendeskMessengerReady = () => typeof window.zE === 'function';

export const ZendeskVisibilityProvider = ({ children }: Props) => {
  const featureFetched = useSelector(areFeaturesFetched);

  const [isZendeskMessagingVisible, setIsZendeskMessagingVisible] =
    useState<boolean>(true);

  const hideZendeskMessaging = () => setIsZendeskMessagingVisible(false);
  const showZendeskMessaging = () => setIsZendeskMessagingVisible(true);

  const { locale } = useIntl();
  const localRef = useRef(locale);
  const user = useSelector(getUser);
  const userRef = useRef(user);
  const zendeskLoadPromiseRef = useRef<Promise<boolean> | null>(null);
  const isZendeskMessengerOpenRef = useRef(false);
  const hasBoundMessengerStateRef = useRef(false);

  const prefillUserInfo = useCallback(() => {
    const currentUser = userRef.current;
    if (!currentUser) {
      return;
    }

    window.zE?.('messenger:set', 'conversationFields', [
      {
        id: '24931382245913',
        value: currentUser.ugcFullName ?? '',
      },
      {
        id: '25001506929049',
        value: currentUser.email ?? '',
      },
      {
        id: '900012280106',
        value: currentUser.CountryCode?.toLowerCase() ?? '',
      },
      {
        id: '62467651214105',
        value: currentUser.whatsappNumber || currentUser.phone || '',
      },
      {
        id: '62467439612697',
        value: 'ingin_pasang_loker_/_rekrut_kandidat',
      },
    ]);
  }, []);

  useEffect(() => {
    userRef.current = user;
    prefillUserInfo();
  }, [user, prefillUserInfo]);

  useEffect(() => {
    localRef.current = locale;
  }, [locale]);

  const loadZendeskMessaging = useCallback(() => {
    if (zendeskLoadPromiseRef.current) {
      return zendeskLoadPromiseRef.current;
    }

    const zeSnippetId = 'ze-snippet';
    const loadPromise = new Promise<boolean>(resolve => {
      const loadTimeoutId = setTimeout(
        () => failLoad(new Error('Timed out to load Zendesk')),
        ZENDESK_LOAD_TIMEOUT
      );

      const settle = (isLoaded: boolean) => {
        clearTimeout(loadTimeoutId);
        resolve(isLoaded);
      };

      if (isZendeskMessengerReady()) {
        settle(true);
        return;
      }

      const failLoad = (error: Error) => {
        document.getElementById(zeSnippetId)?.remove();
        zendeskLoadPromiseRef.current = null;
        settle(false);
        apm.captureError(error);
      };

      const handleError = () => failLoad(new Error('Failed to load Zendesk'));

      const handleLoad = () => {
        if (!isZendeskMessengerReady()) {
          handleError();
          return;
        }

        const previousLocale = localStorage.getItem(
          ZENDESK_CONVERSATION_LOCALE_KEY
        );
        const zendeskLocale =
          localRef.current === SupportedLanguages.id
            ? localRef.current
            : SupportedLanguages.en;

        const shouldResetWidget =
          previousLocale !== null && previousLocale !== zendeskLocale;

        if (previousLocale === null) {
          localStorage.setItem(ZENDESK_CONVERSATION_LOCALE_KEY, zendeskLocale);
        }

        const initZendesk = () => {
          if (!hasBoundMessengerStateRef.current) {
            hasBoundMessengerStateRef.current = true;
            window.zE('messenger:on', 'open', () => {
              isZendeskMessengerOpenRef.current = true;
            });
            window.zE('messenger:on', 'close', () => {
              isZendeskMessengerOpenRef.current = false;
            });
          }

          window.zE('messenger:set', 'locale', zendeskLocale);
          prefillUserInfo();
          settle(true);
        };

        if (shouldResetWidget) {
          window.zE('messenger', 'resetWidget', () => {
            initZendesk();
            localStorage.setItem(
              ZENDESK_CONVERSATION_LOCALE_KEY,
              zendeskLocale
            );
          });
        } else {
          initZendesk();
        }
      };

      const existingScript = document.getElementById(zeSnippetId);

      if (existingScript) {
        existingScript.addEventListener('load', handleLoad, { once: true });
        existingScript.addEventListener('error', handleError, { once: true });
        return;
      }

      const zenDeskScript = document.createElement('script');
      zenDeskScript.id = zeSnippetId;
      zenDeskScript.src = `https://static.zdassets.com/ekr/snippet.js?key=${config.ZENDESK_WIDGET_KEY}`;
      zenDeskScript.addEventListener('load', handleLoad, { once: true });
      zenDeskScript.addEventListener('error', handleError, { once: true });
      document.body.appendChild(zenDeskScript);
    });

    zendeskLoadPromiseRef.current = loadPromise;
    return loadPromise;
  }, [prefillUserInfo]);

  const openZendeskMessaging = useCallback(async () => {
    try {
      const isLoaded = await loadZendeskMessaging();

      if (!isLoaded) {
        return false;
      }

      if (isZendeskMessengerOpenRef.current) {
        window.zE('messenger', 'open');
        return true;
      }

      const isOpened = await new Promise<boolean>(resolve => {
        let settled = false;

        const done = (opened: boolean) => {
          if (settled) {
            return;
          }

          settled = true;
          clearTimeout(timeoutId);

          resolve(opened);
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        };

        const unsubscribe = window.zE(
          'messenger:on',
          'conversationOpened',
          () => done(true)
        );
        const timeoutId = setTimeout(() => done(false), ZENDESK_OPEN_TIMEOUT);
        window.zE('messenger', 'open');
      });

      return isOpened;
    } catch (err) {
      apm.captureError(err as Error);
      return false;
    }
  }, [loadZendeskMessaging]);

  useEffect(() => {
    if (!featureFetched) {
      return;
    }

    const timer = setTimeout(loadZendeskMessaging, ZENDESK_LAZY_LOAD_TIMEOUT);
    return () => clearTimeout(timer);
  }, [featureFetched, loadZendeskMessaging]);

  const isZendeskWidgetVisible = useMemo(
    () => isZendeskMessagingVisible,
    [isZendeskMessagingVisible]
  );

  useEffect(() => {
    try {
      if (window.zE) {
        window.zE(
          'messenger:set',
          'zIndex',
          isZendeskWidgetVisible ? 99999 : -99999
        );
      }
    } catch (error) {
      // Since Zendesk is 3rd party lib. any failure to it should not cause entire UI to fail
      // Catch and report error to avoid showing fallback UI due to unhandled error
      console.error(error);
    }
  }, [isZendeskWidgetVisible]);

  return (
    <ZendeskVisibilityContext.Provider
      value={{
        showZendeskMessaging,
        hideZendeskMessaging,
        isZendeskMessagingVisible,
        openZendeskMessaging,
      }}
    >
      {children}
    </ZendeskVisibilityContext.Provider>
  );
};
